import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import { useLoginContext } from '../../context';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Popover } from 'antd';
import moment from 'moment';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EqualizerIcon from '@mui/icons-material/Equalizer';

import { isSelected, moveTrack, KeyCode } from '../../utils';
import { analyzeSelection, moveRange, calculateInsertBefore, SelectionInfo } from '../../utils/multiDragUtils';
import { ITEMS_PER_PAGE } from '../../constants/common';
import { SongsTableStyle } from './SongsTable.style';
import SongRowSkeleton, { SongRowSkeletonList } from './SongRowSkeleton';

interface VirtualizedMobileSongsTableProps {
  state: any[];
  setState: (songs: any[]) => void;
  selectedSongs: any[];
  bulkAction: boolean;
  setBulkAction: (value: boolean) => void;
  currentPage: number;
  selectSongFunction: (obj: any, id: any, index: number) => void;
  selectSingleSong: (obj: any, id: any, index: number) => void;
  selectMultpleSongFunction?: (arr: any[], idArr: any[]) => void;
  loadMoreTracks?: () => void;
  hasMoreTracks?: boolean;
  playlistState?: any;
  menu?: any;
  hideMenu?: boolean;
  HideDates?: boolean;
  playlistDispatch?: any;
  onSongsReorder?: (oldIndex: number, newIndex: number, count?: number) => void;
  isSongplaceOnly?: boolean;
  clearSelection?: () => void;
}

// Sortable Mobile Row Component
interface SortableMobileRowProps {
  index: number;
  song: any;
  selectedSongs: any[];
  bulkAction: boolean;
  setBulkAction?: (value: boolean) => void;
  currentPage: number;
  selectSongFunction: (song: any, index: number) => void;
  selectSingleSong: (song: any, index: number) => void;
  rangeSongSelect?: (song: any, index: number) => void;
  isSelectedFn: (song: any, selectedSongs: any[], currentPage: number, index: number) => boolean;
  playlistState?: any;
  menu?: any;
  hideMenu?: boolean;
  HideDates?: boolean;
  playlistDispatch?: any;
  SearchId?: string;
  disableDrag?: boolean;
}

const SortableMobileRow: React.FC<SortableMobileRowProps> = ({
  index,
  song,
  selectedSongs,
  bulkAction,
  setBulkAction,
  currentPage,
  selectSongFunction,
  selectSingleSong,
  rangeSongSelect,
  isSelectedFn,
  playlistState,
  menu,
  hideMenu,
  HideDates,
  playlistDispatch,
  SearchId,
  disableDrag = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: song.uniqueId || song.id || song._id,
    disabled: disableDrag,
  });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleRowClick = (e: React.MouseEvent) => {
    if (isDragging) return;

    if (!bulkAction && setBulkAction) {
      setBulkAction(true);
    }

    const ctrlHold = e.ctrlKey || e.metaKey || localStorage.getItem('ctrlHold') === 'true';
    const shiftHold = e.shiftKey || localStorage.getItem('shiftHold') === 'true';

    if (ctrlHold) {
      selectSongFunction(song, index);
    } else if (shiftHold && rangeSongSelect) {
      rangeSongSelect(song, index);
    } else {
      selectSingleSong(song, index);
    }
  };

  const memoizedMenu = (songData: any) => {
    if (menu && typeof menu === 'function') {
      menu(songData);
    }
  };

  const handleAddSong = (songData: any) => {
    if (songData?.addSongHandle) {
      songData.addSongHandle(songData);
    }
  };

  const isRowSelected = isSelectedFn(song, selectedSongs, currentPage, index);

  // This is the EXACT same structure as SongsTableNew MobTable
  return (
    <div
      ref={setNodeRef}
      style={dragStyle}
      className={`rowMob ${isRowSelected ? 'selected' : ''}`}
      onClick={handleRowClick}
    >
      {/* Play button / Song number - EXACT copy from SongsTableNew */}
      {playlistState?.previewSong?.id === song?.id ? (
        <div style={{ padding: '6px 0px 0px 0px ' }}>
          <EqualizerIcon
            style={{
              marginLeft: '15px',
            }}
            className="icons45"
          />
        </div>
      ) : (
        <div
          className="playButtonPlayList"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          {...(disableDrag ? {} : attributes)}
          {...(disableDrag ? {} : listeners)}
        >
          <PlayArrowIcon
            style={{
              color: 'white',
            }}
            className="icons1"
          />
          <span
            className="icons2"
            style={{
              color: 'white',
            }}
          >
            {song?.rowNumber?.indexes}
          </span>
        </div>
      )}

      {/* Song details - EXACT copy from SongsTableNew */}
      <div className="songDetail">
        <div>
          {typeof song?.image === 'string' ? (
            <div className="imageName">
              <span className="imgSpan">
                <span
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    song?.clickHandle(
                      song?.previewUrl,
                      song?.id,
                      song?.image,
                      song?.title,
                      song?.artist,
                      song?.externalUrl
                    );
                  }}
                >
                  <img
                    className="table-list-image"
                    alt="thumbnail"
                    src={song?.image}
                    width="40"
                    height="40"
                  />
                </span>
              </span>
              <div className="namePlaylist">
                <>
                  <p
                    id="hancker"
                    onClick={(e) => {
                      e.stopPropagation();
                      song?.clickHandle(
                        song?.previewUrl,
                        song?.id,
                        song?.image,
                        song?.title,
                        song?.artist,
                        song?.externalUrl
                      );
                    }}
                  >
                    {song?.title}
                  </p>
                  <p
                    id="hancker"
                    onClick={(e) => {
                      e.stopPropagation();
                      song?.clickHandle(
                        song?.previewUrl,
                        song?.id,
                        song?.image,
                        song?.title,
                        song?.artist,
                        song?.externalUrl
                      );
                    }}
                  >
                    {' '}
                    {song?.artist}
                  </p>
                </>
              </div>
            </div>
          ) : (
            <div className="imageName">
              <span className="imgSpan">{song?.image}</span>
              <div className="namePlaylist">{song?.name}</div>
            </div>
          )}
        </div>
        {HideDates ? null : (
          <div className="songTimeDetail">
            <table className="startEndDate">
              <tbody>
                <tr>
                  <td>
                    <p>Start date:</p>
                  </td>
                  <td>
                    <h3 className="startEndTime">
                      <Popover
                        content={
                          <p className="hanker2">
                            {moment(song?.added_at).format('llll')}
                          </p>
                        }
                        placement="left"
                        title="Start Time"
                      >
                        <p
                          id="hancker1"
                          onClick={(e) => {
                            e.stopPropagation();
                            song?.clickHandle(
                              song?.previewUrl,
                              song?.id,
                              song?.image,
                              song?.title,
                              song?.artist,
                              song?.externalUrl
                            );
                          }}
                        >
                          {' '}
                          {moment(song?.added_at).format('DD/MM/YYYY')}
                        </p>
                      </Popover>
                    </h3>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p>End date:</p>
                  </td>
                  <td>
                    <h3 className="startEndTime">{song?.endTime}</h3>
                  </td>
                </tr>
              </tbody>
            </table>
            <a
              className="playSpot"
              target="blank"
              href={'https://open.spotify.com/playlist/' + SearchId}
              onClick={(e) => e.stopPropagation()}
            >
              <img alt="Play on Spotify" src={'./../static/images/spotifyLogo.png'} />
              <p style={{ cursor: 'pointer' }}>Play on Spotify</p>
            </a>
          </div>
        )}
        {!hideMenu && menu ? (
          <svg
            cursor={'pointer'}
            onClick={(e) => {
              e.stopPropagation();
              memoizedMenu(song);
            }}
            stroke="currentColor"
            fill="none"
            strokeWidth="0"
            viewBox="0 0 24 24"
            height="26px"
            width="26px"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M2 5H14V7H2V5Z" fill="currentColor"></path>
            <path d="M2 9H14V11H2V9Z" fill="currentColor"></path>
            <path d="M10 13H2V15H10V13Z" fill="currentColor"></path>
            <path
              d="M16 9H18V13H22V15H18V19H16V15H12V13H16V9Z"
              fill="currentColor"
            ></path>
          </svg>
        ) : song?.Edit ? (
          <div onClick={(e) => e.stopPropagation()}>
            {song?.Edit({ ...song, latestindex: index })}
          </div>
        ) : null}
        {hideMenu ? (
          <img
            alt="plusicon"
            src={'./static/icons_plus.svg'}
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleAddSong(song);
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

// Multi-Drag Overlay for Mobile
interface MobileDragOverlayProps {
  songs: any[];
  count: number;
}

const MobileDragOverlay: React.FC<MobileDragOverlayProps> = ({ songs, count }) => {
  const firstSong = songs[0];
  
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #1a2332 0%, #111823 100%)',
        borderRadius: '12px',
        padding: '12px 16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        minWidth: '200px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <img
          src={firstSong?.image}
          alt="thumbnail"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '6px',
            objectFit: 'cover',
          }}
        />
        <div>
          <p style={{ color: 'white', fontSize: '14px', margin: 0, fontWeight: 500 }}>
            {count} songs selected
          </p>
          <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>
            Drag to reorder
          </p>
        </div>
      </div>
    </div>
  );
};

const VirtualizedMobileSongsTable: React.FC<VirtualizedMobileSongsTableProps> = ({
  state,
  setState,
  selectedSongs,
  bulkAction,
  setBulkAction,
  currentPage,
  selectSongFunction,
  selectSingleSong,
  selectMultpleSongFunction,
  loadMoreTracks,
  hasMoreTracks,
  playlistState,
  menu,
  hideMenu,
  HideDates,
  playlistDispatch,
  onSongsReorder,
  isSongplaceOnly,
  clearSelection,
}) => {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { playlistId } = useParams();
  const { state: loginState } = useLoginContext();

  const SearchId = playlistState?.selectedPlaylistId || '';

  // State for multi-selection functionality
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // State for drag overlay functionality
  const [activeDragItem, setActiveDragItem] = useState<any>(null);

  // State for multi-drag selection analysis
  const [selectionInfo, setSelectionInfo] = useState<SelectionInfo>({
    isConsecutive: true,
    startIndex: -1,
    endIndex: -1,
    count: 0,
    selectedIndices: [],
  });

  // Use refs to avoid dependency issues with scroll listener
  const hasMoreTracksRef = useRef(hasMoreTracks);
  const loadMoreTracksRef = useRef(loadMoreTracks);

  useEffect(() => {
    hasMoreTracksRef.current = hasMoreTracks;
  }, [hasMoreTracks]);

  useEffect(() => {
    loadMoreTracksRef.current = loadMoreTracks;
  }, [loadMoreTracks]);

  // Manual scroll listener for infinite loading
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const isDragging = localStorage.getItem('isDragging') === 'true';

      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      const isNearBottom = distanceFromBottom < 100;

      if (isNearBottom && hasMoreTracksRef.current && loadMoreTracksRef.current && !isDragging) {
        loadMoreTracksRef.current();
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // Keyboard event listeners for multi-selection
  useEffect(() => {
    localStorage.setItem('ctrlHold', 'false');
    localStorage.setItem('shiftHold', 'false');

    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.keyCode === KeyCode('ctrl')) {
        localStorage.setItem('ctrlHold', 'true');
      }
      if (ev.keyCode === KeyCode('shift')) {
        localStorage.setItem('shiftHold', 'true');
      }
    };

    const handleKeyUp = (ev: KeyboardEvent) => {
      if (ev.keyCode === KeyCode('ctrl')) {
        localStorage.setItem('ctrlHold', 'false');
      }
      if (ev.keyCode === KeyCode('shift')) {
        localStorage.setItem('shiftHold', 'false');
      }
    };

    const handleWindowBlur = () => {
      localStorage.setItem('ctrlHold', 'false');
      localStorage.setItem('shiftHold', 'false');
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowBlur);
    };
  }, []);

  // Analyze selection whenever selectedSongs or state changes
  useEffect(() => {
    const info = analyzeSelection(selectedSongs, state);
    setSelectionInfo(info);
  }, [selectedSongs, state]);

  // Compute whether drag should be allowed based on selection
  const canDrag = useMemo(() => {
    if (!selectedSongs || selectedSongs.length <= 1) return true;
    return selectionInfo.isConsecutive;
  }, [selectedSongs, selectionInfo]);

  // Drag and drop sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  // Selection callback functions
  const selectSongFn = useCallback(
    (a: any, i: number) => {
      selectSongFunction(
        {
          uri: a?.rowNumber?.e?.item.uri,
          positions: [
            a.lockPosition ? Number(a.lockPositionNumber) : i + (currentPage - 1) * ITEMS_PER_PAGE,
          ],
        },
        a._id,
        i
      );
      setSelectedIndex(i);
    },
    [selectSongFunction, currentPage]
  );

  const selectSingleSongFn = useCallback(
    (a: any, i: number) => {
      selectSingleSong(
        {
          uri: a?.rowNumber?.e?.item.uri,
          positions: [
            a.lockPosition ? Number(a.lockPositionNumber) : i + (currentPage - 1) * ITEMS_PER_PAGE,
          ],
        },
        a._id,
        i
      );
      setSelectedIndex(i);
    },
    [selectSingleSong, currentPage]
  );

  const rangeSongSelect = useCallback(
    (a: any, toSelect: number) => {
      const anchorIndex = selectedIndex !== null ? selectedIndex : toSelect;

      let idArr: string[] = [];
      let arr: any[] = [];
      state?.forEach((b, eachSong) => {
        if (
          (eachSong >= anchorIndex && toSelect >= eachSong) ||
          (eachSong <= anchorIndex && toSelect <= eachSong)
        ) {
          if (b._id) {
            idArr.push(b._id);
          }
          arr.push({
            uri: b?.rowNumber?.e?.item.uri,
            positions: [
              b.lockPosition
                ? Number(b.lockPositionNumber)
                : eachSong + (currentPage - 1) * ITEMS_PER_PAGE,
            ],
          });
        }
      });

      if (selectMultpleSongFunction) {
        selectMultpleSongFunction(arr, idArr);
      }
    },
    [state, selectedIndex, currentPage, selectMultpleSongFunction]
  );

  // Drag start handler
  const handleDragStart = useCallback((event: DragStartEvent) => {
    if (selectedSongs && selectedSongs.length > 1 && !selectionInfo.isConsecutive) {
      toast.warning(
        'Drag-and-drop only works for consecutive songs. Please select songs in a continuous range.'
      );
      return;
    }

    localStorage.setItem('isDragging', 'true');

    if (selectedSongs && selectedSongs.length > 1 && selectionInfo.isConsecutive) {
      const selectedItems = state.slice(
        selectionInfo.startIndex,
        selectionInfo.endIndex + 1
      );
      setActiveDragItem({
        isMulti: true,
        items: selectedItems,
        ...selectedItems[0],
      });
    } else {
      const draggedSong = state.find(
        item => (item.uniqueId || item.id || item._id) === event.active.id
      );
      setActiveDragItem({ isMulti: false, ...draggedSong });
    }
  }, [state, selectedSongs, selectionInfo]);

  // Drag end handler
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      localStorage.setItem('isDragging', 'false');
      const dragItem = activeDragItem;
      setActiveDragItem(null);

      setTimeout(() => {
        if (hasMoreTracks && loadMoreTracks && localStorage.getItem('dragTouchedBottom') === 'true') {
          localStorage.setItem('dragTouchedBottom', 'false');
          loadMoreTracks();
        }
      }, 100);

      if (!over || active.id === over.id) return;

      const dropTargetIndex = state.findIndex(
        item => (item.uniqueId || item.id || item._id) === over.id
      );

      if (dropTargetIndex === -1) return;

      if (dragItem?.isMulti && selectionInfo.isConsecutive && selectionInfo.count > 1) {
        // Multi-drag path
        const { startIndex, count } = selectionInfo;
        const newState = moveRange(state, startIndex, count, dropTargetIndex);
        setState(newState);

        const insertBefore = calculateInsertBefore(startIndex, count, dropTargetIndex);

        try {
          await moveTrack({
            token: loginState.token,
            playlistId: playlistId,
            rangeStart: startIndex,
            insertBefore,
            rangeLength: count,
          });

          if (onSongsReorder) {
            onSongsReorder(startIndex, dropTargetIndex, count);
          }

          if (clearSelection) {
            clearSelection();
          }

          toast.success(`Moved ${count} songs successfully`);
        } catch (error: any) {
          console.error('Failed to move tracks:', error);
          setState(state);

          if (error.response?.status === 401) {
            toast.error('Session expired. Please refresh the page and try again.');
          } else {
            toast.error('Failed to move songs. Please try again.');
          }
        }
      } else {
        // Single-drag path
        const oldIndex = state.findIndex(
          item => (item.uniqueId || item.id || item._id) === active.id
        );
        const newIndex = dropTargetIndex;

        if (oldIndex === -1) return;

        const newState = arrayMove(state, oldIndex, newIndex);
        setState(newState);

        const rangeStart = oldIndex;
        const insertBefore = newIndex < oldIndex ? newIndex : newIndex + 1;

        try {
          await moveTrack({
            token: loginState.token,
            playlistId: playlistId,
            rangeStart,
            insertBefore,
            rangeLength: 1,
          });

          if (onSongsReorder) {
            onSongsReorder(oldIndex, newIndex);
          }
        } catch (error: any) {
          console.error('Failed to move track:', error);
          setState(state);

          if (error.response?.status === 401) {
            toast.error('Session expired. Please refresh the page and try again.');
          } else {
            toast.error('Failed to move song. Please try again.');
          }
        }
      }
    },
    [
      state,
      setState,
      loginState.token,
      playlistId,
      hasMoreTracks,
      loadMoreTracks,
      onSongsReorder,
      activeDragItem,
      selectionInfo,
      clearSelection,
    ]
  );

  // Item renderer for Virtuoso
  const ItemComponent = useCallback((index: number) => {
    const song = state[index];

    return (
      <SortableMobileRow
        key={song.uniqueId || song.id || song._id}
        index={index}
        song={song}
        selectedSongs={selectedSongs}
        bulkAction={bulkAction}
        setBulkAction={setBulkAction}
        currentPage={currentPage}
        selectSongFunction={selectSongFn}
        selectSingleSong={selectSingleSongFn}
        rangeSongSelect={rangeSongSelect}
        isSelectedFn={isSelected}
        playlistState={playlistState}
        menu={menu}
        hideMenu={hideMenu}
        HideDates={HideDates}
        playlistDispatch={playlistDispatch}
        SearchId={SearchId}
        disableDrag={selectedSongs && selectedSongs.length > 1 && !canDrag}
      />
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, selectedSongs, bulkAction, setBulkAction, currentPage, selectSongFn, selectSingleSongFn, rangeSongSelect, playlistState, menu, hideMenu, HideDates, playlistDispatch, SearchId, canDrag]);

  // Footer component for infinite loading
  const Footer = useCallback(() => {
    if (!hasMoreTracks || isSongplaceOnly) return null;

    return <SongRowSkeletonList count={3} variant="mobile" />;
  }, [hasMoreTracks, isSongplaceOnly]);

  const ScrollSeekPlaceholder = useCallback(
    ({ height }: { height: number }) => <SongRowSkeleton height={height} variant="mobile" />,
    []
  );

  // Infinite scroll handler
  const endReached = useCallback(() => {
    const isDragging = localStorage.getItem('isDragging') === 'true';
    if (hasMoreTracks && loadMoreTracks && !isDragging) {
      loadMoreTracks();
    } else {
      localStorage.setItem('dragTouchedBottom', 'true');
    }
  }, [hasMoreTracks, loadMoreTracks]);

  // Single drag overlay component
  const SingleDragOverlay = useCallback(() => {
    if (!activeDragItem) return null;

    return (
      <div
        style={{
          background: 'linear-gradient(135deg, #1a2332 0%, #111823 100%)',
          borderRadius: '12px',
          padding: '12px 16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <img
          src={activeDragItem?.image}
          alt="thumbnail"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '6px',
            objectFit: 'cover',
          }}
        />
        <div>
          <p style={{ color: 'white', fontSize: '14px', margin: 0, fontWeight: 500 }}>
            {activeDragItem?.title?.length > 20
              ? activeDragItem?.title.substring(0, 20) + '...'
              : activeDragItem?.title}
          </p>
          <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>
            {activeDragItem?.artist?.length > 15
              ? activeDragItem?.artist.substring(0, 15) + '...'
              : activeDragItem?.artist}
          </p>
        </div>
      </div>
    );
  }, [activeDragItem]);

  return (
    <SongsTableStyle>
      <div
        ref={containerRef}
        className="mobTablePlaylist"
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToFirstScrollableAncestor, restrictToVerticalAxis]}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          autoScroll={{
            enabled: true,
            acceleration: 10,
          }}
        >
          <SortableContext
            items={state.map(item => item.uniqueId || item.id || item._id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="innerMobTablePlaylist">
              <Virtuoso
                ref={virtuosoRef}
                style={{ height: 'calc(100vh - 520px)', minHeight: '300px' }}
                totalCount={state.length}
                itemContent={ItemComponent}
                endReached={endReached}
                components={{ Footer, ScrollSeekPlaceholder }}
                overscan={5}
                increaseViewportBy={{ top: 400, bottom: 800 }}
                scrollSeekConfiguration={{
                  enter: velocity => Math.abs(velocity) > 200,
                  exit: velocity => Math.abs(velocity) < 30,
                }}
                computeItemKey={(index) => state[index]?.uniqueId || state[index]?.id || state[index]?._id || index}
              />
            </div>
          </SortableContext>
          <DragOverlay>
            {activeDragItem?.isMulti ? (
              <MobileDragOverlay
                songs={activeDragItem.items}
                count={selectionInfo.count}
              />
            ) : (
              <SingleDragOverlay />
            )}
          </DragOverlay>
        </DndContext>
      </div>
    </SongsTableStyle>
  );
};

export default VirtualizedMobileSongsTable;

